import { Component, OnInit } from '@angular/core';
import { SupabaseService } from '../../../shared/services/supabase.service';
import { TranslationService } from '../../../shared/services/translation.service';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import {
  UserService,
  parseFacilityLimits,
} from '../../../shared/services/user.service';
import { MatIcon } from '@angular/material/icon';
import { ClubService } from '../../../shared/services/club.service';
import { FacilitiesService } from '../../../shared/services/facilities.service';
import {
  ClubFacility,
  FacilityLimit,
} from '../../../shared/models/clubs.models';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-subscription-banner',
  imports: [TranslatePipe, MatIcon, CommonModule],
  templateUrl: './subscription-banner.component.html',
  styleUrl: './subscription-banner.component.scss',
})
export class SubscriptionBannerComponent implements OnInit {
  subscription_expiration_date: string | undefined;
  subscription_package: string | undefined;
  daysLeft: number = 0;
  hasActiveSubscription = false;

  // ── Facilities ────────────────────────────────────────────────────────────
  facilitiesEnabled = false;
  facilityIds: string[] | null = null;
  /** Per facility caps from the same row (`facility_limits`). */
  facilityLimits: FacilityLimit[] = [];
  facilities: ClubFacility[] = [];

  constructor(
    private supabaseService: SupabaseService,
    private translationService: TranslationService,
    private userService: UserService,
    private clubService: ClubService,
    private facilitiesService: FacilitiesService
  ) {}

  async ngOnInit() {
    const flags = this.clubService.flags();
    this.facilitiesEnabled = (flags?.subscriptions && flags?.facilities) ?? false;
    await this.getUserSubscriptionPlan();
  }

  private async getUserSubscriptionPlan() {
    const userId = this.userService.currentUser?.id;
    if (!userId) return;

    // Club-scoped + active-only. The old `get_user_subscription_by_id` RPC
    // filtered ONLY by user_id (no club_id, no active filter), so a member's
    // subscription from ANOTHER club — or an inactive/cancelled one — leaked in
    // and rendered a false "active" banner. Read the completion table directly,
    // scoped to THIS club and active rows only.
    // A renewed member keeps one active row per period, so pick the latest
    // period (as the admin list does) and read its facilities from that same row.
    const fetchRow = (columns: string) =>
      this.supabaseService.supabase
        .from('users_subscription_completion')
        .select(columns)
        .eq('user_id', userId)
        .eq('club_id', this.supabaseService.clubId)
        .eq('active', true)
        .order('expiration', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

    // `facility_limits` is newer than the generated types, hence the cast.
    // Until its migration is applied PostgREST answers 42703 (unknown
    // column); retry without it so the banner never disappears.
    let res = await fetchRow('package, expiration, facility_ids, facility_limits');
    if (res.error?.code === '42703') {
      res = await fetchRow('package, expiration, facility_ids');
    }
    const { error } = res;
    const sub = res.data as unknown as SubscriptionBannerRow | null;

    if (error) {
      console.error(error);
      return;
    }
    if (!sub || !sub.expiration) return;

    this.daysLeft = getDaysFromToday(sub.expiration);
    if (this.daysLeft < 0) return;

    this.hasActiveSubscription = true;
    sessionStorage.setItem('subscription_expiration_date', sub.expiration);
    this.subscription_expiration_date =
      this.translationService.getFullDateTranslation(new Date(sub.expiration));
    this.subscription_package = sub.package
      ? this.translationService.translateDays(sub.package, 'SUBSCRIPTIONS')
      : undefined;

    // Load facility access when the flag is on.
    if (this.facilitiesEnabled) {
      // Init pulls facilities + sports lookup that `sportNameFor` needs.
      await this.facilitiesService.init();
      this.facilities = await this.facilitiesService.getFacilities();
      this.facilityIds = sub.facility_ids ?? null;
      this.facilityLimits = parseFacilityLimits(sub.facility_limits);
    }
  }

  facilityName(id: string): string {
    const f = this.facilities.find((x) => x.id === id);
    return f ? this.facilitiesService.sportNameFor(f) || id : id;
  }

  facilityColor(id: string): string {
    return this.facilities.find(f => f.id === id)?.color ?? '#16a34a';
  }

  /** Facility_ids resolved to NAMED facilities. Unnamed/unknown ids are dropped
   *  so a raw UUID can never leak into the UI. An unrestricted subscription
   *  can still cap single facilities: those get a chip next to "All facilities". */
  get accessChips(): AccessChip[] {
    const ids = this.facilityIds?.length
      ? this.facilityIds
      : this.facilityLimits.map((l) => l.facility_id);
    return ids
      .map((id) => this.facilities.find((f) => f.id === id))
      .filter((f): f is ClubFacility => !!f)
      // A facility has no `name` column — its label comes from the linked
      // sport via `sportNameFor`. Drop any id that doesn't resolve to a sport
      // name so a raw UUID can never leak into the UI.
      .map((f) => ({
        id: f.id,
        name: this.facilitiesService.sportNameFor(f) || '',
        color: f.color ?? '#16a34a',
        limit: this.facilityLimits.find((l) => l.facility_id === f.id) ?? null,
      }))
      .filter((chip) => chip.name.trim().length > 0);
  }

  /** Show the access row when the subscription is unrestricted ("All facilities")
   *  or when at least one facility resolves to a name. If it is facility-restricted
   *  but none resolve, hide the row entirely rather than render raw ids. */
  get showFacilityRow(): boolean {
    if (!this.facilityIds?.length) return true;
    return this.accessChips.length > 0;
  }

  protected readonly Math = Math;
}

interface SubscriptionBannerRow {
  package: string | null;
  expiration: string | null;
  facility_ids: string[] | null;
  /** Missing until the facility_limits migration is applied. */
  facility_limits?: unknown;
}

interface AccessChip {
  id: string;
  name: string;
  color: string;
  limit: FacilityLimit | null;
}

function getDaysFromToday(dateString: string): number {
  // 1. Validate the input format
  const parts = dateString.split('-');
  if (parts.length !== 3) {
    return NaN; // Or throw an error
  }

  // 2. Parse year, month, and day as integers
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const day = parseInt(parts[2], 10);

  // Check for parsing errors
  if (isNaN(year) || isNaN(month) || isNaN(day)) {
    return NaN;
  }

  // 3. Create the target date in UTC
  // The month is 0-indexed in JavaScript (0 = January, 11 = December)
  const targetDateUTC = Date.UTC(year, month - 1, day);

  // 4. Get today's date in UTC
  const today = new Date();
  const todayUTC = Date.UTC(
    today.getUTCFullYear(),
    today.getUTCMonth(),
    today.getUTCDate()
  );

  // 5. Calculate the difference
  const differenceInMs = targetDateUTC - todayUTC;
  const differenceInDays = Math.floor(differenceInMs / (1000 * 60 * 60 * 24));

  return differenceInDays;
}
